import React, { useState, useCallback } from "react";
import moment from "moment";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import domtoimage from "dom-to-image";
import { round } from "./utils";
import CustomTooltipContent from "./CustomTooltipContent";
import { ReactComponent as ZoomIn } from "./zoom_in-24px.svg";
import { ReactComponent as ZoomOut } from "./zoom_out-24px.svg";
import { ReactComponent as SaveBtn } from "./add_photo_alternate-24px.svg";
import MinMaxModal from "./MinMaxModal";
import { ReactComponent as QueryIcon } from "./images/query_stats.svg";

function gen4() {
  return Math.random().toString(16).slice(-4);
}

function simpleUniqueId(prefix) {
  return (prefix || "") + Array(8).fill().map(gen4).join("");
}

function CustomizedAxisTick({ x, y, payload }) {
  const value = moment(Number(payload.value)).format("D.M. HH:mm");
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#666"
        transform="rotate(-35)"
      >
        {value}
      </text>
    </g>
  );
}

export default function Chart(props) {
  const {
    data,
    title,
    xName,
    average,
    unit,
    divider = 1,
    sensorName,
  } = props;


  const hasData =
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[data.length - 1][xName] !== "undefined";

  let dataUntil = sensorName?.until;
  if (dataUntil > 0) {
    if (dataUntil < 15719230590) dataUntil *= 1000;
    if (Date.now() > dataUntil) return null;
  }

  if (!hasData) return null;

  const [maximized, setMaximized] = useState(false);
  const [minMaxModalOpen, setMinMaxModalOpen] = useState(false);
  const [id] = useState(() => simpleUniqueId("chart-"));

  // Tasmota plug: show yesterday's data in long time graph
  const dataType = xName === "power" ? "yesterday" : xName;
  const xName2 = xName + "2";

  const toggleMaximize = useCallback(() => setMaximized((prev) => !prev), []);
  const openMinMaxModal = useCallback(() => setMinMaxModalOpen(true), []);
  const closeMinMaxModal = useCallback(() => setMinMaxModalOpen(false), []);

  const saveChart = useCallback(() => {
    const fileName =
      (sensorName.name || sensorName.deviceid) +
      "_" +
      title +
      "_" +
      moment().format("D.M.YYYY HH:mm") +
      ".svg";
    domtoimage
      .toSvg(document.getElementById(id), { bgcolor: "#ffffff" })
      .then((blob) => {
        window.saveAs(blob, fileName);
      });
  }, [sensorName, title, id]);

  return (
    <>
      <MinMaxModal
        isOpen={minMaxModalOpen}
        onClose={closeMinMaxModal}
        deviceid={sensorName.deviceid}
        name=""
        dataType={dataType}
      />
      <div className={maximized ? "chart chart-maximized" : "chart"}>
        <div className="title-with-action">
          <h3>{title}</h3>
          <button onClick={toggleMaximize}>
            {maximized ? (
              <>
                <ZoomOut title="Pienennä" role="button" width="40" />
                Pienennä
              </>
            ) : (
              <>
                <ZoomIn title="Suurenna" role="button" width="40" />
                Suurenna
              </>
            )}
          </button>
          <button onClick={saveChart}>
            <SaveBtn title="Tallenna" role="button" width="40" />
            Tallenna
          </button>
          <button className="btn-save" onClick={openMinMaxModal}>
            <QueryIcon />
            Pitkä
          </button>
        </div>
        <div id={id} style={{ minHeight: 450 }}>
          <ResponsiveContainer height={450} width="90%">
            <LineChart
              data={data}
              syncId="syncId"
              margin={{
                top: 5,
                right: 20,
                left: -10,
                bottom: 45,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                scale="time"
                type="number"
                interval="preserveEnd"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(x) =>
                  moment(Number(x)).format("D.M. HH:mm")
                }
                tick={<CustomizedAxisTick />}
              />
              <YAxis
                scale="linear"
                allowDataOverflow={true}
                reversed={false}
                tickFormatter={(formats) => round(formats / divider, 1)}
                domain={["dataMin", "dataMax"]}
              />
              <Tooltip
                contentStyle={{
                  background: "none",
                  border: "none",
                  fontSize: "12px",
                }}
                itemStyle={{ margin: 0, padding: 0, color: "#333" }}
                content={
                  <CustomTooltipContent name={title} average={average} />
                }
                formatter={(formats) => round(formats / divider, 1) + (unit || "")}
                labelFormatter={(x) =>
                  moment(Number(x)).format("D.M.YYYY HH:mm")
                }
              />
              <ReferenceLine
                y={average.mean}
                stroke="#777777"
                strokeDasharray="3 3"
              />
              <Line
                type="monotone"
                dataKey={xName}
                stroke="#8884d8"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey={xName2}
                stroke="#d88884"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
